import { useNavigate } from 'react-router-dom';
import type { SharedItemInterface } from '../../interfaces/sharedWithMe.interface';

interface SharedItemRowProps {
  item: SharedItemInterface;
  onRead: (storyId: string) => void;
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatSharerNames(sharers: { username: string }[]): string {
  if (sharers.length === 1) return `@${sharers[0].username}`;
  if (sharers.length === 2)
    return `@${sharers[0].username} and @${sharers[1].username}`;
  if (sharers.length === 3)
    return `@${sharers[0].username}, @${sharers[1].username} and @${sharers[2].username}`;
  return `@${sharers[0].username}, @${sharers[1].username} and ${sharers.length - 2} others`;
}

const AVATAR_COLORS = [
  '#6366f1',
  '#0891b2',
  '#059669',
  '#d97706',
  '#dc2626',
  '#7c3aed',
  '#0369a1',
  '#b45309',
];

function getAvatarColor(username: string): string {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

const SharedItemRow = ({ item, onRead }: SharedItemRowProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!item.isRead) {
      onRead(item.storyId);
    }
    navigate(`/post/${item.storyId}`);
  };

  const visibleAvatars = item.sharers.slice(0, 3);
  const overflowCount = item.sharers.length - 3;

  return (
    <div
      className={`shared-item-row ${item.isRead ? 'shared-item-row--read' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      <div className="shared-item-row__left">
        {!item.isRead && <div className="shared-item-row__dot" />}

        <div className="shared-item-row__avatars">
          {visibleAvatars.map((sharer) => (
            <div
              key={sharer.username}
              className="shared-item-row__avatar"
              style={{
                backgroundColor: sharer.avatarUrl
                  ? 'transparent'
                  : getAvatarColor(sharer.username),
              }}
            >
              {sharer.avatarUrl ? (
                <img
                  src={sharer.avatarUrl}
                  alt={sharer.username}
                  className="shared-item-row__avatar-img"
                />
              ) : (
                sharer.avatarInitials
              )}
            </div>
          ))}
          {overflowCount > 0 && (
            <div className="shared-item-row__avatar shared-item-row__avatar--overflow">
              +{overflowCount}
            </div>
          )}
        </div>
      </div>

      <div className="shared-item-row__content">
        <p className="shared-item-row__sharers">
          {formatSharerNames(item.sharers)}
          {item.circles.length > 0 && (
            <span className="shared-item-row__via"> via {item.circles[0]}</span>
          )}
        </p>
        <p className="shared-item-row__title">{truncate(item.title, 50)}</p>
        <p className="shared-item-row__meta">
          {formatRelativeTime(item.sharedAt)}
        </p>

        {item.sharers.length > 1 && (
          <div className="shared-item-row__pips">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className={`shared-item-row__pip ${i < Math.min(item.sharers.length, 5) ? 'shared-item-row__pip--filled' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedItemRow;
